import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Spinner, Pagination, Alert, Form, Row, Col } from 'react-bootstrap';
import { getLeads } from '../../services/api';

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
  });
  
  const PAGE_SIZE = 20;

  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        const params = {
          skip: (page - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
          ...filters
        };

        // Remove empty filter values
        Object.keys(params).forEach(key => {
          if (params[key] === '') {
            delete params[key];
          }
        });

        const data = await getLeads(params);
        setLeads(data.leads);
        setTotalRecords(data.total);
        setTotalPages(Math.ceil(data.total / PAGE_SIZE));
      } catch (err) {
        console.error('Error fetching leads:', err);
        setError('Failed to load leads. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, [page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1); // Reset to first page when filter changes
  };

  const clearFilters = () => {
    setFilters({ status: '' });
    setPage(1);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const renderCibilStatus = (status) => {
    if (status === 'success') {
      return <Badge bg="success">Success</Badge>;
    } else if (status === 'failure') {
      return <Badge bg="danger">Failed</Badge>;
    } else {
      return <Badge bg="warning">Pending</Badge>;
    }
  };

  if (loading && leads.length === 0) {
    return (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>All Leads</h2>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <h5>Filters</h5>
          <Form>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select 
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">Any</option>
                    <option value="pending">Pending</option>
                    <option value="valid">Valid</option>
                    <option value="invalid">Invalid</option>
                    <option value="processed">Processed</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <Button 
                  variant="outline-secondary" 
                  onClick={clearFilters}
                  className="mb-3"
                >
                  Clear Filters
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="mb-3">
            <h5>Leads ({totalRecords})</h5>
          </div>

          {leads.length === 0 ? (
            <div className="text-center p-5">
              <p>No leads match the current filters.</p>
            </div>
          ) : (
            <>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>PAN</th>
                    <th>Email</th>
                    <th>Status</th>
                    <th>CIBIL Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => (
                    <tr key={lead.id}>
                      <td>{lead.name}</td>
                      <td>{lead.mobile}</td>
                      <td>{lead.pan || '-'}</td>
                      <td>{lead.email || '-'}</td>
                      <td>
                        <Badge bg={lead.status === 'valid' ? 'success' : 'secondary'}>
                          {lead.status}
                        </Badge>
                      </td>
                      <td>{renderCibilStatus(lead.cibil_status)}</td>
                      <td>{formatDate(lead.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setPage(1)} 
                      disabled={page === 1} 
                    />
                    <Pagination.Prev 
                      onClick={() => setPage(prev => Math.max(prev - 1, 1))} 
                      disabled={page === 1} 
                    />
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <Pagination.Item 
                          key={pageNum} 
                          active={pageNum === page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    
                    <Pagination.Next 
                      onClick={() => setPage(prev => Math.min(prev + 1, totalPages))} 
                      disabled={page === totalPages} 
                    />
                    <Pagination.Last 
                      onClick={() => setPage(totalPages)} 
                      disabled={page === totalPages} 
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default LeadsPage;